import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarPriceDependents } from "./solar-price-dependents"
import type { SolarProposalPricingDto } from "../dto/solar-proposal-pricing.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type CalculateSolarProposalPricingVariables = {
  proposalId: string
}

/**
 * Refresh Pricing for a Solar Proposal, from the investment section or the
 * System Pricing step. The answer is the pricing read itself, so it replaces
 * that cache entry. A cash system's net savings subtract the stored price, so
 * the savings read refetches too, as does the System Pricing view. The header
 * carries no price, so it stays put.
 */
export default function useCalculateSolarProposalPricing(): UseMutationResult<
  SolarProposalPricingDto,
  unknown,
  CalculateSolarProposalPricingVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId }: CalculateSolarProposalPricingVariables) =>
      solarProposalService.postPricing(proposalId),
    onSuccess: (pricing, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.pricing(proposalId),
        pricing,
      )
      invalidateSolarPriceDependents(queryClient, proposalId, {
        except: [`pricing`, `header`],
      })
      // Refresh Pricing refreshes incentives against the new price.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.incentives(proposalId),
        exact: true,
      })
    },
    // A superseded refresh means the inputs moved under it, and a failure
    // after the first pricing (the incentive reprice) leaves that price and the
    // refreshed incentives stored: every price read and incentives read again.
    onError: (_error, { proposalId }) => {
      invalidateSolarPriceDependents(queryClient, proposalId, {
        except: [`header`],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.incentives(proposalId),
        exact: true,
      })
    },
  })
}
